import React, { useState } from 'react';
import SideMenu from '@ajat/design-system/SideMenu';
import Button from '@ajat/design-system/Button';
import { ComponentPage, DemoCard, DemoRow } from '../shared';
import { DEMO_SIDE_MENU } from './demoData';

export const meta = {
  component: SideMenu,
  componentName: 'SideMenu',
  title: 'Side Menu',
  group: 'Navigation',
  importPath: '@ajat/design-system/SideMenu',
  figmaNode: '2388:6010',
  description:
    'OVER/LL navigation panel that slides in from the left, floating 12px from the screen edges — 480px wide (capped at the viewport), full height. Gray-075 header with title + close, then a featured NavigationCard with a button, link cards two a row, plain links under a rule, and an optional actions bar pinned to the bottom (phones only by default) — each block renders only when its data is passed. Escape, the overlay, the X and any menu item close it; body scroll is locked while open. Switch the brand to Overall or Crownstudent to see it as designed (Crownstudent shows its own content — Crownstudent-MCP 52:16398). On phones it is a push drawer: the page slides aside, a handle on the panel edge shows the swipe-to-close. The DS Header renders it itself from its `sideMenu` prop (hamburger → SideMenu) — see the Header page with the Overall brand.',
  props: [
    ['isOpen', 'bool', 'false', 'Open state'],
    ['onClose', 'func', '—', 'Fires after the slide-out (X, overlay, Escape, a menu item)'],
    ['title', 'node', "'Menu'", 'Header title'],
    ['featured', '{image, imageAlt?, imageFit?, imageAspectRatio?, label, href?, onClick?}', '—', 'Full-width card with a Secondary button. imageAspectRatio (e.g. \'3 / 2\' — Crown Student) overrides the NavigationCard 276 / 221 frame'],
    ['cards', '[{image, imageAlt?, imageFit?, imageAspectRatio?, label, href?, onClick?}]', '[]', 'Link cards, two a row (any count — Crown Student has four)'],
    ['links', '[{label, href?, onClick?}]', '[]', 'Plain a.Regular links under a gray-0 rule'],
    ['actions', '[{label, icon?, onClick?}]', '[]', 'Text buttons (icon + label) pinned to the panel bottom, split by a USPV2 hairline — the body scrolls above them. Phones only unless actionsOnDesktop'],
    ['actionsOnDesktop', 'bool', 'false', 'Show actions ≥1024px too (default hidden — the desktop header carries them as icons)'],
    ['closeOnNavigate', 'bool', 'true', 'Close after a card / link click (its onClick runs first)'],
    ['closeLabel', 'string', "'Close'", 'Accessible name of the X button (and the phone swipe handle)'],
    ['pushTarget', 'string', "'#root'", 'Phones (< 1024px): selector of the page element the open menu pushes aside (Figma 2778:29896) — the panel sits flush left, the page slides right by its width, a swipe to the left (or the handle / X) closes it. null = no push'],
    ['preview', 'bool', 'false', 'Static in-flow render — no overlay, portal, motion or scroll lock'],
    ['className', 'string', "''", 'Extra class on the panel'],
    ['children', 'node', '—', 'Extra content after the links'],
  ],
  usage: [
    {
      title: 'From the DS Header hamburger (Overall)',
      code: `// The Header renders the SideMenu itself and owns its open state
// (closes on routeKey changes). title / closeLabel fall back to the
// Header's labels.menu / labels.closeMenu.
<Header
  brand="Overall"
  routeKey={location.key}
  sideMenu={{
    title: 'Welcome',
    featured: { image: editions, label: 'DESIGNA EDITIONS', onClick: toSection('editions') },
    cards: [{ image: patch, imageFit: 'contain', label: 'Patch Universe', href: '#patches' }],
    links: [{ label: 'About Us', href: '/about' }],
  }}
/>`,
    },
    {
      title: 'Standalone',
      code: `import SideMenu from '@ajat/design-system/SideMenu';

const [menuOpen, setMenuOpen] = useState(false);

<SideMenu
  isOpen={menuOpen}
  onClose={() => setMenuOpen(false)}
  title="Welcome"
  featured={{ image: editions, label: 'DESIGNA EDITIONS', onClick: () => navigate(OVERALL_CONFIGURATOR) }}
  cards={[
    { image: patch, imageFit: 'contain', label: 'Patch Universe', href: '#patches' },
    { image: quality, label: 'Quality', href: '#quality' },
  ]}
  links={[
    { label: 'About Us', href: '/about' },
    { label: 'Customer Service', href: '/contact' },
  ]}
/>`,
    },
  ],
};

export default function SideMenuPage({ brand = 'CLS' }) {
  const [open, setOpen] = useState(false);
  // Crownstudent has its own content (Crownstudent-MCP 52:16398); every other brand shows OVER/LL's
  const MENU = DEMO_SIDE_MENU[brand] ?? DEMO_SIDE_MENU.Overall;

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Live" hint="slides in from the left — Escape, the X, the overlay or a menu item close it">
        <DemoRow align="center">
          <Button size="secondary" variant="main" onClick={() => setOpen(true)}>Open side menu</Button>
        </DemoRow>
        <SideMenu
          isOpen={open}
          onClose={() => setOpen(false)}
          {...MENU}
        />
      </DemoCard>

      <DemoCard title="Preview" hint="Figma 2388:6010 — static render">
        <SideMenu
          preview
          {...MENU}
          actionsOnDesktop
        />
      </DemoCard>
    </ComponentPage>
  );
}
